/**
 * /delete-account — the page both stores insist on.
 *
 * Google's User Data policy wants a web address where somebody can delete their account WITHOUT
 * reinstalling the app, and asks for that address in the Data safety form. Apple wants the same
 * thing reachable inside the app. This is the web half; the app links here and also deletes in
 * place. It therefore sits outside the auth gate, like /privacy — a person who cannot sign in is
 * exactly the person most likely to arrive here.
 *
 * It deletes for real: emo_users/account_deletion.py removes the account, its transcripts and
 * clips, and the user's files in S3, and cancels any subscription. That is why the button asks
 * for the account's own email first, and why the page says plainly what will not come back.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import apiRequest from "../../utils/apiClient";
import { hostApi, EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { brandPageCss } from "../brand/brandPageCss";
import { BrandNav, BrandFooter, useBrandTheme } from "../brand/BrandChrome";
import {
  DELETE_TITLE,
  DELETE_INTRO,
  DELETE_GONE_HEADING,
  DELETE_GONE,
  DELETE_KEPT_HEADING,
  DELETE_KEPT,
  DELETE_CONFIRM_LABEL,
  DELETE_BUTTON,
  DELETE_WORKING,
  DELETE_DONE,
  DELETE_SIGNED_OUT,
  DELETE_BY_EMAIL,
} from "./deleteAccountConstants";

const SCOPE = "ccda";

const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useBrandTheme();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError("");
    try {
      const body = new URLSearchParams({ confirm: email.trim() });
      const res = await apiRequest(`${hostApi}delete-account/`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setError(DELETE_SIGNED_OUT); return; }
      if (!res.ok) { setError(data.error || "That did not work. Try again."); return; }
      setDone(true);
      try { localStorage.clear(); } catch (err) { /* private mode */ }
    } catch (err) {
      setError("Could not reach CherryClipz. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + PAGE_CSS}</style>

      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PRIVACY)}>Privacy</button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.CONTACT)}>Help</button>
      </BrandNav>

      <div className="wrap hero">
        <h1>{DELETE_TITLE}</h1>
        <p className="sub">{DELETE_INTRO}</p>
      </div>

      <section className="wrap doc">
        {done ? (
          <div className="done" role="status">
            <h2>{DELETE_DONE.heading}</h2>
            {DELETE_DONE.paragraphs.map((p) => <p key={p}>{p}</p>)}
          </div>
        ) : (
          <>
            <div className="cols">
              <div>
                <h2>{DELETE_GONE_HEADING}</h2>
                <ul>{DELETE_GONE.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
              <div>
                <h2>{DELETE_KEPT_HEADING}</h2>
                <ul>{DELETE_KEPT.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            </div>

            <form className="form" onSubmit={submit}>
              <label htmlFor="cc-del-email">{DELETE_CONFIRM_LABEL}</label>
              <input
                id="cc-del-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
              {error ? <p className="err" role="alert">{error}</p> : null}
              <button type="submit" className="danger" disabled={busy || !email.trim()}>
                {busy ? DELETE_WORKING : DELETE_BUTTON}
              </button>
            </form>

            <p className="fallback">
              {DELETE_BY_EMAIL} <a href={`mailto:${EMO_EMAIL}?subject=Delete%20my%20CherryClipz%20account`}>{EMO_EMAIL}</a>
            </p>
          </>
        )}
      </section>

      <BrandFooter />
    </div>
  );
};

const PAGE_CSS = `
.ccda .doc { padding-bottom: 72px; }
.ccda .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-bottom: 34px; }
@media (max-width: 720px) { .ccda .cols { grid-template-columns: 1fr; } }
.ccda .doc h2 { font-size: 18px; margin: 0 0 10px; }
.ccda .doc ul { margin: 0; padding-left: 18px; }
.ccda .doc li { margin-bottom: 8px; line-height: 1.5; }
.ccda .form { display: flex; flex-direction: column; gap: 10px; max-width: 420px; }
.ccda .form label { font-weight: 700; }
.ccda .form input { padding: 12px 14px; border-radius: 10px; border: 1px solid currentColor; font: inherit; background: transparent; color: inherit; }
.ccda .form .danger { padding: 13px 18px; border-radius: 999px; border: 0; font: inherit; font-weight: 800; color: #fff; background: #c4163f; cursor: pointer; }
.ccda .form .danger:disabled { opacity: .5; cursor: default; }
.ccda .err { color: #c4163f; font-weight: 700; margin: 0; }
.ccda .fallback { margin-top: 26px; opacity: .8; }
.ccda .done h2 { font-size: 22px; }
`;

export default DeleteAccountPage;
